import { useEffect, useState } from 'react';
import { Button, Chip, Stack } from '@mui/material';
import { MyProxyGivenToDto } from '../../services/dtos/meeting.dto';
import { giveMeetingProxy, revokeMeetingProxy } from '../../services/general-meeting.service';
import { useSnackbars } from '../../hooks/snackbar.hook';
import { showAxiosMessageError } from '../../utils/error-message.util';
import MeetingProxyDialog from './meeting-proxy-dialog';

interface AttendanceMeetingLike {
    id: string;
    attendance?: boolean | null;
    status: string;
    suiviPresence?: boolean;
    delegationPouvoirsActivee?: boolean;
    myProxyGivenTo?: MyProxyGivenToDto | null;
}

const normalizeStatus = (status: string): string =>
    status
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase();

const resolveAttendanceLabel = (attendance?: boolean | null): string => {
    if (attendance === true) {
        return 'Présent';
    }

    if (attendance === false) {
        return 'Absent';
    }

    return 'Sans réponse';
};

const resolveAttendanceColor = (attendance?: boolean | null): 'default' | 'success' | 'error' => {
    if (attendance === true) {
        return 'success';
    }

    if (attendance === false) {
        return 'error';
    }

    return 'default';
};

const canRespondToMeeting = (meeting: AttendanceMeetingLike): boolean =>
    !['terminee', 'annulee', 'reportee'].includes(normalizeStatus(meeting.status));

interface MeetingAttendanceProps {
    meeting: AttendanceMeetingLike;
    loading?: boolean;
    compact?: boolean;
    onChange: (attendance: boolean) => void | Promise<unknown>;
    onProxyChange?: (myProxyGivenTo: MyProxyGivenToDto | null) => void;
}

export default function MeetingAttendance(props: Readonly<MeetingAttendanceProps>) {
    const [proxyDialogOpen, setProxyDialogOpen] = useState(false);
    const [proxyLoading, setProxyLoading] = useState(false);
    const [myProxyGivenTo, setMyProxyGivenTo] = useState<MyProxyGivenToDto | null | undefined>(
        props.meeting.myProxyGivenTo
    );
    const showMessage = useSnackbars();

    useEffect(() => {
        setMyProxyGivenTo(props.meeting.myProxyGivenTo);
    }, [props.meeting.id, props.meeting.myProxyGivenTo]);

    if (!props.meeting.suiviPresence) {
        return null;
    }

    const canDelegate = Boolean(props.meeting.delegationPouvoirsActivee) && props.meeting.attendance === false;

    const handleChange = async (attendance: boolean) => {
        await props.onChange(attendance);

        if (!attendance && props.meeting.delegationPouvoirsActivee && !myProxyGivenTo) {
            setProxyDialogOpen(true);
        }
    };

    const handleGiveProxy = async (target: 'member' | 'suppleant', recipientReunionEluId?: string) => {
        setProxyLoading(true);
        try {
            const result = await giveMeetingProxy(props.meeting.id, target, recipientReunionEluId);
            setMyProxyGivenTo(result.myProxyGivenTo);
            props.onProxyChange?.(result.myProxyGivenTo);
            setProxyDialogOpen(false);
        } catch (error) {
            showAxiosMessageError(error as Error, showMessage);
        } finally {
            setProxyLoading(false);
        }
    };

    const handleRevokeProxy = async () => {
        setProxyLoading(true);
        try {
            await revokeMeetingProxy(props.meeting.id);
            setMyProxyGivenTo(null);
            props.onProxyChange?.(null);
        } catch (error) {
            showAxiosMessageError(error as Error, showMessage);
        } finally {
            setProxyLoading(false);
        }
    };

    return (
        <Stack
            direction={props.compact ? { xs: 'column', sm: 'row' } : 'row'}
            spacing={1}
            alignItems={props.compact ? { xs: 'stretch', sm: 'center' } : 'center'}
        >
            <Chip
                size="small"
                color={resolveAttendanceColor(props.meeting.attendance)}
                label={resolveAttendanceLabel(props.meeting.attendance)}
                variant={
                    props.meeting.attendance === undefined || props.meeting.attendance === null ? 'outlined' : 'filled'
                }
            />
            {canRespondToMeeting(props.meeting) && (
                <>
                    <Button
                        size="small"
                        variant={props.meeting.attendance === true ? 'contained' : 'outlined'}
                        disabled={props.loading}
                        onClick={() => handleChange(true)}
                    >
                        Accepter
                    </Button>
                    <Button
                        size="small"
                        color="inherit"
                        variant={props.meeting.attendance === false ? 'contained' : 'outlined'}
                        disabled={props.loading}
                        onClick={() => handleChange(false)}
                    >
                        Refuser
                    </Button>
                </>
            )}
            {canDelegate && canRespondToMeeting(props.meeting) && (
                <Stack direction="row" spacing={1} alignItems="center">
                    {myProxyGivenTo ? (
                        <>
                            <Chip size="small" color="info" label={`Pouvoir donné à ${myProxyGivenTo.displayName}`} />
                            <Button size="small" disabled={proxyLoading} onClick={handleRevokeProxy}>
                                Annuler le pouvoir
                            </Button>
                        </>
                    ) : (
                        <Button
                            size="small"
                            variant="outlined"
                            disabled={proxyLoading}
                            onClick={() => setProxyDialogOpen(true)}
                        >
                            Donner mon pouvoir
                        </Button>
                    )}
                </Stack>
            )}
            <MeetingProxyDialog
                open={proxyDialogOpen}
                meetingId={props.meeting.id}
                loading={proxyLoading}
                onClose={() => setProxyDialogOpen(false)}
                onGiveToMember={recipientReunionEluId => handleGiveProxy('member', recipientReunionEluId)}
                onGiveToSuppleant={() => handleGiveProxy('suppleant')}
            />
        </Stack>
    );
}
